import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Archive, Calendar, FileText, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { archiveChart, MONITORING_FREQUENCIES } from './api';
import { BowelChartComplianceBadge } from './BowelChartComplianceBadge';

const frequencyLabel = (v) => MONITORING_FREQUENCIES.find(f => f.value === v)?.label || v || '—';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const categoryBadgeClass = (cat) => {
  if (cat === 'clinical') return 'bg-rose-50 border-rose-200 text-rose-700';
  return 'bg-slate-50 border-slate-200 text-slate-700';
};

export function ParticipantChartList({ charts, participantId, canArchive, onChanged }) {
  const navigate = useNavigate();
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [pending, setPending] = useState(false);

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      setPending(true);
      await archiveChart(participantId, archiveTarget.id);
      toast.success(`"${archiveTarget.name}" archived`);
      setArchiveTarget(null);
      onChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.error?.message || 'Failed to archive chart';
      toast.error(msg);
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <div className="rounded-md border overflow-hidden" data-testid="charts-list">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Template</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {charts.map(c => (
              <TableRow key={c.id} data-testid={`chart-row-${c.id}`}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="font-medium">{c.name}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground font-mono">{c.template_code}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] ${categoryBadgeClass(c.category)}`}>
                    {c.category || '—'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {c.status === 'archived' ? (
                    <Badge variant="outline" className="text-[10px] bg-slate-100 border-slate-300 text-slate-600">Archived</Badge>
                  ) : (
                    <div className="flex flex-col items-start gap-0.5">
                      <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-300 text-emerald-700">Active</Badge>
                      {c.compliance && <BowelChartComplianceBadge compliance={c.compliance} />}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-xs">{frequencyLabel(c.monitoring_frequency)}</TableCell>
                <TableCell className="text-xs">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-muted-foreground" />
                    {formatDate(c.created_at)}
                  </div>
                </TableCell>
                {canArchive && (
                  <TableCell className="text-right whitespace-nowrap">
                    {c.template_code === 'bowel_chart' && c.status !== 'archived' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={() => navigate(`/participants/${participantId}/charts/${c.id}`)}
                        data-testid={`chart-open-btn-${c.id}`}
                      >
                        Open<ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                      </Button>
                    )}
                    {c.status !== 'archived' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-muted-foreground hover:text-red-600"
                        onClick={() => setArchiveTarget(c)}
                        data-testid={`chart-archive-btn-${c.id}`}
                      >
                        <Archive className="w-3.5 h-3.5 mr-1" />Archive
                      </Button>
                    )}
                  </TableCell>
                )}
                {!canArchive && c.template_code === 'bowel_chart' && c.status !== 'archived' && (
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => navigate(`/participants/${participantId}/charts/${c.id}`)}
                      data-testid={`chart-open-btn-${c.id}`}
                    >
                      Open<ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="chart-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive chart</AlertDialogTitle>
            <AlertDialogDescription>
              Archive <strong>{archiveTarget?.name}</strong>? Archived charts are hidden from the active list but their history is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={pending}
              data-testid="chart-archive-confirm-btn"
            >
              {pending ? 'Archiving…' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
